/**
 * Pure column-definition builder for the run table.
 *
 * Extracted from DataExplorer so it can be unit-tested without pulling
 * in React, AG Grid, or Supabase transitive imports.
 */

import type { ColDef } from "ag-grid-community";
import { ALL_AGG_FUNC_NAMES } from "./aggFunctions";

export interface RunTableColumn {
  field: string;
  headerName: string;
}

const HIDDEN_FIELDS = new Set(["_group", "_path_prefix", "_node_id", "_geometry_node_id", "_is_stale", "_stale_reason", "_resolved_from"]);
const DEFAULT_HIDDEN_SUFFIX = "_f360";
const CATEGORY_SUFFIXES = ["_name", "_status", "_filename", "_format", "_units", "_version"];
const CATEGORY_EXACT = new Set(["name", "kind", "status", "solver_version", "filename", "format", "visualization_status", "velocity_units", "ref_area_units_f360", "sweep_group", "_source"]);

const DEFAULT_AGG: Record<string, string> = {
  CL: "max",
  CD: "min",
  CM: "min",
};

export function isCategory(field: string): boolean {
  if (CATEGORY_EXACT.has(field)) return true;
  return CATEGORY_SUFFIXES.some((s) => field.endsWith(s));
}

export function isHiddenField(field: string): boolean {
  return HIDDEN_FIELDS.has(field);
}

/**
 * Detect the best columns to group by for "Smart Group".
 *
 * Returns fields with few unique values relative to total rows (categorical
 * or low-cardinality). Always includes `_source` when present.
 *
 * Accepts optional overrides to force-include or exclude specific fields.
 */
export function detectSmartGroupColumns(
  rows: Record<string, unknown>[],
  columns: RunTableColumn[],
  overrides?: { include?: string[]; exclude?: string[] },
): string[] {
  if (rows.length === 0) return [];

  const excludeSet = new Set(overrides?.exclude ?? []);
  const includeSet = new Set(overrides?.include ?? []);

  const candidates: string[] = [];
  for (const col of columns) {
    if (HIDDEN_FIELDS.has(col.field)) continue;
    if (col.field.startsWith("_") && col.field !== "_source") continue;
    if (excludeSet.has(col.field)) continue;

    // User-forced include
    if (includeSet.has(col.field)) {
      const unique = new Set(rows.map((r) => r[col.field]));
      if (unique.size > 1) candidates.push(col.field);
      continue;
    }

    const unique = new Set(rows.map((r) => r[col.field]));
    const ratio = unique.size / rows.length;

    if (col.field === "_source" || isCategory(col.field)) {
      if (unique.size > 1) candidates.push(col.field);
    } else if (ratio < 0.3 && unique.size > 1 && unique.size <= 20) {
      candidates.push(col.field);
    }
  }

  return candidates;
}

/**
 * Auto-detect the best column to color scatter plots by.
 *
 * Prefers `_source`, then any categorical column with 2-10 unique values.
 */
export function detectBestColorBy(
  rows: Record<string, unknown>[],
  columns: RunTableColumn[],
): string | undefined {
  if (rows.length === 0) return undefined;

  const hasSource = columns.some((c) => c.field === "_source");
  if (hasSource) {
    const unique = new Set(rows.map((r) => r._source));
    if (unique.size > 1 && unique.size <= 10) return "_source";
  }

  for (const col of columns) {
    if (HIDDEN_FIELDS.has(col.field)) continue;
    if (!isCategory(col.field)) continue;
    if (col.field === "_source") continue;
    const unique = new Set(rows.map((r) => r[col.field]));
    if (unique.size > 1 && unique.size <= 10) return col.field;
  }

  return undefined;
}

/**
 * Build AG Grid column definitions from run table columns.
 *
 * When `showUncorrected` is true, `_f360` columns are revealed, corrected
 * columns gain a "(corrected)" suffix, and pairs are ordered adjacently.
 */
export type DisplayPrecision = number | null;
export type ColumnPrecisionMap = Record<string, DisplayPrecision>;

export function buildColumnDefs(
  columns: RunTableColumn[],
  showUncorrected: boolean,
  precision: DisplayPrecision = null,
  columnPrecision: ColumnPrecisionMap = {},
): ColDef[] {
  const f360Fields = new Set(
    columns
      .filter((c) => c.field.endsWith(DEFAULT_HIDDEN_SUFFIX))
      .map((c) => c.field.replace(/_f360$/, "")),
  );

  const summaryDefs: ColDef[] = [
    {
      field: "__alpha_stall",
      headerName: "α_stall",
      aggFunc: "α at CL_max",
      hide: true,
      enableValue: true,
      allowedAggFuncs: ALL_AGG_FUNC_NAMES,
    },
    {
      field: "__alpha_ldmax",
      headerName: "α @ L/D_max",
      aggFunc: "α at L/D_max",
      hide: true,
      enableValue: true,
      allowedAggFuncs: ALL_AGG_FUNC_NAMES,
    },
  ];

  const cols: ColDef[] = columns
    .filter((col) => !HIDDEN_FIELDS.has(col.field))
    .map((col) => {
      const cat = isCategory(col.field);
      const isF360 = col.field.endsWith(DEFAULT_HIDDEN_SUFFIX);
      const hasCorrectedTwin = f360Fields.has(col.field);

      let displayName: string;
      if (isF360) {
        displayName = col.field.replace(/_f360$/, " (f360)");
      } else if (showUncorrected && hasCorrectedTwin) {
        displayName = `${col.headerName} (corrected)`;
      } else if (col.field === "_source") {
        displayName = "Source";
      } else if (col.field.startsWith("cfg_")) {
        displayName = col.headerName.replace(/^cfg_/, "");
      } else {
        displayName = col.headerName;
      }

      const effectivePrecision = col.field in columnPrecision ? columnPrecision[col.field] : precision;
      const numericFormatter = !cat && effectivePrecision != null
        ? { valueFormatter: (p: { value: unknown }) => typeof p.value === "number" ? p.value.toFixed(effectivePrecision) : String(p.value ?? "") }
        : {};

      return {
        field: col.field,
        headerName: displayName,
        sortable: true,
        filter: true,
        resizable: true,
        enableRowGroup: true,
        enableValue: !cat,
        allowedAggFuncs: ALL_AGG_FUNC_NAMES,
        ...(DEFAULT_AGG[col.field] ? { aggFunc: DEFAULT_AGG[col.field] } : {}),
        chartDataType: (cat ? "category" : "series") as "category" | "series",
        ...numericFormatter,
        ...(col.field === "name" ? { pinned: "left" as const, width: 200 } : {}),
        ...(col.field === "_source" ? { pinned: "left" as const, width: 140 } : {}),
        ...(col.field === "sweep_group" ? { hide: true, rowGroup: false, headerName: "Sweep Group" } : {}),
        ...(isF360 ? { hide: !showUncorrected } : {}),
      };
    });

  if (showUncorrected) {
    const ordered: ColDef[] = [];
    const used = new Set<string>();
    for (const col of cols) {
      if (used.has(col.field!)) continue;
      used.add(col.field!);
      ordered.push(col);
      if (f360Fields.has(col.field!)) {
        const twin = cols.find((c) => c.field === `${col.field}${DEFAULT_HIDDEN_SUFFIX}`);
        if (twin && !used.has(twin.field!)) {
          used.add(twin.field!);
          ordered.push(twin);
        }
      }
    }
    return [...ordered, ...summaryDefs];
  }

  return [...cols, ...summaryDefs];
}
